import { useRef } from 'react';
import styled from 'styled-components';
import { Skeleton, Tooltip, Typography } from '@mui/material';
import Humanize from 'humanize-plus';
import { PieChart, Pie, Cell } from 'recharts';
import { ReactComponent as InfoIcon } from '../../../assets/info-icon.svg';
import { Flex, FlexCenter, FlexColumn } from '../../../components/Flex';
import useHover from '../../../hooks/useHover';
import { colorTokens } from '../../../style/tokens';
import { fontSize, fontWeight } from '../../../style/utils';

const VALUE_FONT_SIZE = '26px';

const RADIAN = Math.PI / 180;

const PieChartStyled = styled(PieChart)`
  * {
    outline: none;
  }
`;

const LabelText = styled.text<{ $disabled: boolean }>`
  ${({ $disabled }) =>
    !$disabled &&
    `
        cursor: pointer;
        :hover {
          text-decoration: underline;
        }
    `}
`;

const Link = styled(Typography)`
  cursor: pointer;
  :hover {
    text-decoration: underline;
  }
`;

const TitleWrapper = styled(Flex)`
  justify-content: center;
  align-items: center;
`;

const Title = styled(Typography)`
  font-size: ${fontSize('xl')};
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
  color: ${({ theme }) => theme.palette.content.secondary};
`;

const renderCustomLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  value,
  name,
  onClick,
  disabled,
}: any) => {
  const radius = innerRadius + (outerRadius - innerRadius) * 1.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <Tooltip
      title={Humanize.intComma(value)}
      open={value < 1000 ? false : undefined}
    >
      <LabelText
        x={x}
        y={y}
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        fontSize="16px"
        fill={colorTokens.icon.primary}
        onClick={() => onClick && onClick(name)}
        $disabled={disabled}
      >
        {Humanize.compactInteger(value, 1).replace('.0', '')}
      </LabelText>
    </Tooltip>
  );
};

type Props = {
  data: {
    name: string;
    value: number;
    color: string;
  }[];
  value: string;
  label: string;
  title: string;
  tooltip: string;
  onLabelClick?: (name: string) => void;
  onValueClick: () => void;
  loading?: boolean;
};

const DashboardPieChart = ({
  data,
  value,
  label,
  title,
  tooltip,
  onLabelClick,
  onValueClick,
  loading,
}: Props) => {
  const wrapperRef = useRef<any>();
  const isHover = useHover(wrapperRef);

  return (
    <FlexColumn ref={wrapperRef} sx={{ gap: 1 }}>
      <TitleWrapper>
        <Flex sx={{ gap: 0.7, alignItems: 'center', ml: '19px' }}>
          <Title>{title}</Title>
          <Tooltip
            title={tooltip}
            sx={{ visibility: isHover ? 'visible' : 'hidden' }}
          >
            <Flex>
              <InfoIcon />
            </Flex>
          </Tooltip>
        </Flex>
      </TitleWrapper>
      <PieChartStyled
        width={250}
        height={200}
        aria-label={`${title} pie chart`}
      >
        {!loading && (
          <foreignObject width="100%" height="100%">
            <FlexColumn
              sx={{
                width: '100%',
                height: '100%',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Tooltip
                title={Humanize.intComma(Number(value))}
                open={Number(value) < 1000 ? false : undefined}
              >
                <Link
                  sx={{ fontSize: VALUE_FONT_SIZE }}
                  onClick={onValueClick}
                  aria-label={`${title} total value`}
                >
                  {Humanize.compactInteger(Number(value), 1).replace('.0', '')}
                </Link>
              </Tooltip>

              <Typography
                sx={{
                  fontSize: fontSize('md'),
                  fontWeight: fontWeight('light'),
                }}
              >
                {label}
              </Typography>
            </FlexColumn>
          </foreignObject>
        )}
        {loading ? (
          <foreignObject width="100%" height="100%">
            <FlexCenter>
              <Skeleton
                sx={{ mt: 3 }}
                variant="circular"
                width={160}
                height={160}
              />
            </FlexCenter>
          </foreignObject>
        ) : (
          <Pie
            data={data.filter((entry) => entry.value > 0)}
            innerRadius={60}
            outerRadius={80}
            dataKey="value"
            minAngle={12}
            label={(props) =>
              renderCustomLabel({
                ...props,
                disabled: !onLabelClick,
                onClick: onLabelClick,
              })
            }
            labelLine={false}
          >
            {data
              .filter((entry) => entry.value > 0)
              .map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.color}
                  strokeWidth={0}
                />
              ))}
          </Pie>
        )}
      </PieChartStyled>
    </FlexColumn>
  );
};

export default DashboardPieChart;
